import CableIcon from "@mui/icons-material/Cable";
import { IconButton } from "@mui/material";
import { type FC, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../contexts/AuthProvider";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { RawPunchesDialog } from "./RawPunchesDialog";

type Props = {
	/** The actual shift id (nurse_in_shift_actual id) whose raw punches to reveal. */
	shiftId: string;
	/** The shift's nurse — enables the dialog's jump to that shift's sync change log. */
	nurseId?: number;
};

/**
 * DAT-221: SUPPORT-ONLY icon button that reveals the raw HRIS punches behind a shift.
 * Renders nothing for non-support users. Clicking opens {@link RawPunchesDialog}; the click is
 * `stopPropagation`'d so it never triggers the surrounding row/cell interaction (day-view filter,
 * profile cell editor). Uses the same `CableIcon` treatment as `ExternalTimeOffIndicator` (the HRIS
 * "synced from" indicator) so both support-only raw-HRIS reveals read as one consistent affordance.
 *
 * Generic over any shift id — drop it onto any shift surface and pass `shiftId`.
 */
export const RawPunchesChip: FC<Props> = ({ shiftId, nurseId }) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const isSupport = !!userInfo?.support;
	const [open, setOpen] = useState(false);

	if (!isSupport) return null;

	const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setOpen(true);
	};

	return (
		<>
			<InHouseTooltip title={t("shift.punches.chip.tooltip")}>
				<IconButton
					size="small"
					onClick={handleClick}
					data-testid={`raw-punches-chip-${shiftId}`}
					aria-label={t("shift.punches.chip.tooltip")}
					sx={{ p: "2px" }}
				>
					<CableIcon sx={{ width: "15px", height: "15px", color: "primary.dark" }} />
				</IconButton>
			</InHouseTooltip>
			{open && <RawPunchesDialog shiftId={shiftId} nurseId={nurseId} open={open} onClose={() => setOpen(false)} />}
		</>
	);
};
